import React, { useState, useEffect } from 'react';
import { calculateEmployeeFinancials, calculatePayrollSummary } from '../../utils/payrollCalculations';
import { loadRunEmployees } from '../../services/payrollRunStore';
import '../../styles/sections/payroll.css';

/**
 * Shared expanded-row content for a payroll run: a "Payroll Summary" panel
 * (gross, gov deductions, adjustments, net) + an "Employees" mini-table.
 *
 * The summary panel renders immediately from the run's denormalized parent
 * aggregates (C2); the mini-table lazily loads the per-employee subcollection on
 * expand via loadRunEmployees (C3) — so collapsed rows never pay the deep read.
 * Historical inline runs have no parent `summary` / subcollection: the panel
 * falls back to computing from their inline employees, which loadRunEmployees
 * returns in place.
 *
 * Consumers wrap this in their own <tr><td colSpan=N> cell and pass companyId.
 */

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
  }).format(amount || 0).replace('PHP', '₱');

const getInitials = (name) => {
  if (!name) return '??';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getEmployeeFinancials = (employee) => {
  const result = calculateEmployeeFinancials(employee);
  return {
    gross: result.gross,
    deductions: result.totalDeductions,
    loans: result.totalLoans, // PRD_LoansManagement C8g
    netAdjustments: result.netAdjustments,
    net: result.net,
  };
};

const surnameKey = (e) => {
  if (e.personalInfo?.lastName) return e.personalInfo.lastName.toLowerCase();
  const name = (e.employeeName || '').trim();
  if (!name) return '';
  const parts = name.split(/\s+/);
  return parts[parts.length - 1].toLowerCase();
};
const firstNameKey = (e) => (e.personalInfo?.firstName || '').toLowerCase();

const PayrollRunExpansion = ({ run, companyId }) => {
  const runId = run.runId || run.id;
  const [employees, setEmployees] = useState(null); // null while loading

  // Lazy deep-fetch the per-employee lines on expand (this component only mounts
  // when a row is expanded). Subcollection for converted runs, inline array for
  // historical — resolved by loadRunEmployees via the storageShape marker (C3).
  useEffect(() => {
    let active = true;
    loadRunEmployees(companyId, runId, run)
      .then((emps) => { if (active) setEmployees(emps); })
      .catch((err) => {
        console.error('[PayrollRunExpansion] Failed to load run employees:', err);
        if (active) setEmployees([]);
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, runId]);

  // Summary panel from the denormalized parent aggregates (C2); fall back to a
  // compute for historical runs that have no parent summary.
  const summary = run.summary || calculatePayrollSummary(employees || []);
  const breakdown = summary.deductionBreakdown || { sss: 0, philhealth: 0, pagibig: 0, tax: 0 };

  return (
    <div className="pl-expansion-content">
      <div className="pl-expansion-layout">
        {/* Summary Section */}
        <div className="pl-expansion-summary">
          <div className="pl-expansion-summary__title">Payroll Summary</div>
          <div className="pl-summary-item pl-summary-item--gross">
            <span className="pl-summary-item__label">Total Gross</span>
            <span className="pl-summary-item__value">{formatCurrency(summary.totalGross)}</span>
          </div>
          <div className="pl-summary-item pl-summary-item--deductions">
            <span className="pl-summary-item__label">SSS</span>
            <span className="pl-summary-item__value">-{formatCurrency(breakdown.sss)}</span>
          </div>
          <div className="pl-summary-item pl-summary-item--deductions">
            <span className="pl-summary-item__label">PhilHealth</span>
            <span className="pl-summary-item__value">-{formatCurrency(breakdown.philhealth)}</span>
          </div>
          <div className="pl-summary-item pl-summary-item--deductions">
            <span className="pl-summary-item__label">Pag-IBIG</span>
            <span className="pl-summary-item__value">-{formatCurrency(breakdown.pagibig)}</span>
          </div>
          <div className="pl-summary-item pl-summary-item--deductions">
            <span className="pl-summary-item__label">Tax</span>
            <span className="pl-summary-item__value">-{formatCurrency(breakdown.tax)}</span>
          </div>
          {/* Stored parent summaries written before PRD_LoansManagement carry no
              totalLoans — read as 0, never as a wrong figure (C8, historical default). */}
          <div className="pl-summary-item pl-summary-item--deductions">
            <span className="pl-summary-item__label">Loans</span>
            <span className="pl-summary-item__value">-{formatCurrency(summary.totalLoans || 0)}</span>
          </div>
          <div className={`pl-summary-item pl-summary-item--adjustments ${summary.totalAdjustments >= 0 ? 'pl-summary-item--positive' : 'pl-summary-item--negative'}`}>
            <span className="pl-summary-item__label">Total Adjustments</span>
            <span className="pl-summary-item__value">
              {summary.totalAdjustments >= 0 ? '+' : ''}{formatCurrency(summary.totalAdjustments)}
            </span>
          </div>
          <div className="pl-summary-item pl-summary-item--net">
            <span className="pl-summary-item__label">Total Net Pay</span>
            <span className="pl-summary-item__value">{formatCurrency(summary.totalNet)}</span>
          </div>
        </div>

        {/* Employee List Section */}
        <div className="pl-expansion-employees">
          <div className="pl-expansion-employees__header">
            <span className="pl-expansion-employees__title">Employees</span>
            <span className="pl-expansion-employees__count">{summary.totalEmployees || 0} employees</span>
          </div>
          <table className="pl-mini-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>ID</th>
                <th>Department</th>
                <th className="th-currency">Gross Income</th>
                <th className="th-currency">Gov't Contributions</th>
                <th className="th-currency">Loans</th>
                <th className="th-currency">Adjustments</th>
                <th className="th-currency">Net Pay</th>
              </tr>
            </thead>
            <tbody>
              {employees === null ? (
                <tr>
                  <td colSpan={8} className="td-employee">Loading employees…</td>
                </tr>
              ) : (
                [...employees]
                  .sort((a, b) => {
                    const lastCmp = surnameKey(a).localeCompare(surnameKey(b));
                    return lastCmp !== 0 ? lastCmp : firstNameKey(a).localeCompare(firstNameKey(b));
                  })
                  .map((employee) => {
                    const financials = getEmployeeFinancials(employee);
                    const employeeName = employee.employeeName ||
                      `${employee.personalInfo?.firstName || ''} ${employee.personalInfo?.lastName || ''}`.trim() ||
                      'Unknown';

                    return (
                      <tr key={employee.id || employee.employeeId}>
                        <td className="td-employee">
                          <div className="pl-mini-avatar">{getInitials(employeeName)}</div>
                          <span>{employeeName}</span>
                        </td>
                        <td className="td-id">{employee.employeeId || employee.employeeCode || '-'}</td>
                        <td className="td-dept">{employee.employment?.department || '-'}</td>
                        <td className="td-currency">{formatCurrency(financials.gross)}</td>
                        <td className="td-currency td-deduction">-{formatCurrency(financials.deductions)}</td>
                        <td className="td-currency td-deduction">-{formatCurrency(financials.loans)}</td>
                        <td className={`td-currency ${financials.netAdjustments > 0 ? 'td-adjustment-positive' : financials.netAdjustments < 0 ? 'td-adjustment-negative' : ''}`}>
                          {financials.netAdjustments > 0 ? '+' : ''}{formatCurrency(financials.netAdjustments)}
                        </td>
                        <td className="td-currency td-net">{formatCurrency(financials.net)}</td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PayrollRunExpansion;
